import { SensorRepository, SensorListOptions, SensorListResponse } from '../../domain/repositories/SensorRepository';
import { Sensor } from '../../domain/entities/Sensor';
import { Alert } from '../../domain/entities/Alert';
import { ILoggerService } from '../../domain/services/LoggerService';
import { UserContext } from '../../domain/value-objects/UserContext';
import { RepositoryError } from '../../domain/errors/RepositoryError';
import { BaseFirebaseRepository, FilterCondition } from './BaseFirebaseRepository';

export class FirebaseSensorRepository extends BaseFirebaseRepository<Sensor> implements SensorRepository {
  constructor(logger: ILoggerService) {
    super('sensors', logger);
  }

  protected convertFirestoreToEntity(id: string, data: any): Sensor {
    this.logger.debug(`SensorRepository converting Firestore entity to Sensor`, { 
      id, 
      name: data.name, 
      serialNumber: data.serialNumber,
      organizationId: data.organizationId,
      alertsCount: Object.keys(data.alerts || {}).length
    });
    
    return new Sensor(
      id,
      data.name,
      data.serialNumber,
      data.brand,
      data.model,
      data.locationId,
      data.locationName,
      data.organizationId,
      this.convertAlertsFromFirestore(data.alerts || {}),
      data.isActive ?? true,
      data.lastData || null,
      data.last5Data || [],
      this.convertFirestoreDate(data.createdAt),
      this.convertFirestoreDate(data.updatedAt)
    );
  }

  protected getDefaultSortField(): string {
    return 'name';
  }

  protected mapSortField(sortBy: string): string {
    switch (sortBy) {
      case 'locationName':
        return 'locationName';
      default:
        return sortBy;
    }
  }

  protected applySearchFilter(sensors: Sensor[], search: string): Sensor[] {
    const searchLower = search.toLowerCase();
    return sensors.filter(sensor =>
      sensor.name.toLowerCase().includes(searchLower) ||
      sensor.serialNumber.toLowerCase().includes(searchLower) ||
      sensor.brand.toLowerCase().includes(searchLower) ||
      sensor.model.toLowerCase().includes(searchLower) ||
      sensor.locationName.toLowerCase().includes(searchLower)
    );
  }



  async findAll(options: SensorListOptions = {}, userContext?: UserContext): Promise<SensorListResponse> {
    const {
      organizationId,
      locationId,
      ...queryOptions
    } = options;

    // Build additional filters for specific location if provided
    const additionalFilters: FilterCondition[] = [];
    if (locationId) {
      additionalFilters.push({ field: 'locationId', operator: '==', value: locationId });
    }

    const response = await this.executeQuery(userContext, queryOptions, additionalFilters, [], organizationId);
    
    return {
      sensors: response.items,
      totalCount: response.totalCount,
      totalPages: response.totalPages,
      currentPage: response.currentPage,
      pageSize: response.pageSize
    };
  }

  async countWithUserContext(userContext?: UserContext): Promise<number> {
    return this.countWithFilters(userContext);
  }

  private convertAlertsFromFirestore(alertsData: any): Record<string, Alert> {
    if (!alertsData || typeof alertsData !== 'object') {
      return {};
    }

    const alerts: Record<string, Alert> = {};
    
    for (const [alertId, alertData] of Object.entries(alertsData)) {
      if (alertData && typeof alertData === 'object') {
        const data = alertData as any;
        alerts[alertId] = new Alert(
          alertId,
          data.name || '',
          data.enabled ?? false,
          data.conditions || [],
          data.email || { enabled: false, emails: [] },
          data.sms || { enabled: false, phoneNumbers: [] },
          data.sensorIds || [],
          data.organizationId || '',
          data.repeat || 1,
          data.delayedNotification || 0,
          this.convertFirestoreDate(data.createdAt),
          this.convertFirestoreDate(data.updatedAt)
        );
      }
    }
    
    return alerts;
  }


  async countByOrganizationId(organizationId: string, userContext?: UserContext): Promise<number> {
    return this.countWithFilters(userContext, [], [], organizationId);
  }

  async findBySerialNumber(serialNumber: string, userContext?: UserContext): Promise<Sensor | null> {
    const startTime = Date.now();
    this.logger.logRepositoryRequest(this.collectionName, 'findBySerialNumber', { serialNumber, userContext });

    try {
      const filters: FilterCondition[] = [
        { field: 'serialNumber', operator: '==', value: serialNumber }
      ];

      const response = await this.executeQuery(userContext, { page: 1, pageSize: 1 }, filters);

      const result = response.items.length > 0 ? response.items[0] : null;

      const duration = Date.now() - startTime;
      this.logger.logRepositoryResponse(this.collectionName, 'findBySerialNumber', duration, {
        found: result !== null
      });

      return result;
    } catch (error) {
      const duration = Date.now() - startTime;
      this.logger.logRepositoryError(this.collectionName, 'findBySerialNumber', duration, error);
      throw RepositoryError.queryFailed();
    }
  }
}